import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom'; 
import { 
  LinkedinIcon, 
  TwitterIcon, 
  GithubIcon, BookOpen,
  ShieldCheck, 
  Waves,
  UserX, 
  Instagram
} from 'lucide-react';

import theGeek from '../../assets/images/the_geek.png';
import kikawaStanley from '../../assets/images/kikawa_stanley.jpeg';
import jeffBwire from '../../assets/images/jeff_bwire.jpeg';
import muwangaIbra from '../../assets/images/muwanga_ibra.jpeg';
import kizziriLule from '../../assets/images/kizziri_lule.jpeg';
import magalaGraham from '../../assets/images/magala_graham.jpeg';
import akeraJoyce from '../../assets/images/akera_joyce.jpeg';
import laduHarry from '../../assets/images/ladu_harry.jpeg';
import oloboPatrick from '../../assets/images/olobo_patrick.jpeg';
import fatehDhillon from '../../assets/images/fateh_dhillon.jpeg';
import anaDorin from '../../assets/images/ana_dorin.jpeg';
import nassimbwaHawah from '../../assets/images/nassimbwa_hawah.jpeg';
import phillipa_babirye from '../../assets/images/phillipa_babirye.png';

// Team members data
const teamMembers = [
  {
    name: "F/N Nuru The Geek",
    role: "Chief Security Architect CSA",
    image: theGeek,
    email: "sudoroot523@gmail.com", 
    specialty: "Driving Innovation in DevOps, DevSecOps, Blockchain, AI Cybersecurity, Network Security Architecture & Zero Trust Architectures"
  },
  {
      name: "Kikawa Stanley",
      role: "Systems Security Admin",
      image: kikawaStanley,
      email: "kikaawas@yahoo.com",
      specialty: "Authentication, Authorisation & Access Management Expert (SSO/MFA/AAA)"
  },
  {
      name: "Jeff Bwire",
      role: "Legal & Data Compliance Officer",
      image: jeffBwire,
      email: "bwire177@gmail.com",
      specialty: "Secure Data Deletion & Regulatory Compliance (SDD/RC)"
  },
  {
      name: "Muwanga Ibra",
      role: "Systems Security & Admin",
      image: muwangaIbra,
      email: "ibbrahimmk@gmail.com",
      specialty: "Advanced Hashing, Cryptographic Integrity & System Hardening"
  },
  {
      name: "Kizziri Lule",
      role: "Backup & Recovery Strategist",
      image: kizziriLule,
      email: "josephelijahkatolule@gmail.com",
      specialty: "Disaster Recovery & Ransomware Mitigation"
  },
  {
      name: "Magala Graham",
      role: "Security Product Manager",
      image: magalaGraham,
      email: "magalagraham@gmail.com",
      specialty: "Firewall Product Integration & Marketing Strategy"
  },
  {
      name: "Akera Joyce",
      role: "Crypto Engineer & Tech Support",
      image: akeraJoyce,
      email: "akeraj97@gmail.com",
      specialty: "ECC, Post-Quantum Security & HelpCenter Technical Assistance"
  },
  {
      name: "Ladu Harry",
      role: "Legal & Security Risk Analyst",
      image: laduHarry,
      email: "harry12sylvester12@gmail.com",
      specialty: "Phishing Risk Assessment, Legal Compliance & Social Engineering Analysis"
  },
  {
      name: "Olobo Patrick",
      role: "Network Analysis & HelpCenter",
      image: oloboPatrick,
      email: "olobopatrick466@gmail.com",
      specialty: "Network Pattern Recognition, Threat Analysis & Advanced HelpCenter Support"
  },
  {
      name: "Fateh Dhillon",
      role: "Digital Detetive & Sec Marketing",
      image: fatehDhillon,
      email: "fddhillon99@gmail.com",
      specialty: "Mobile/Email Forensics, Threat Hunting & Security Product Marketing"
  },
  {
      name: "Ana Dorin",
      role: "Financial & Tech Integration",
      image: anaDorin,
      email: "anadorin14@gmail.com",
      specialty: "Facial Recognition for Financial Transactions & Educational Resource Management"
  },
  {
      name: "Nassimbwa Hawah",
      role: "Security Risk & Financial Analyst",
      image: nassimbwaHawah,
      email: "nassimbwahawah@gmail.com",
      specialty: "Vulnerability Scanning, Financial Risk Assessment & Security Compliance Audits"
  },
  {
    name: "Phillipa Babirye",
    role: "Network Analyst & Sec Marketing",
    image: phillipa_babirye,
    email: "phillipaeve1@gmail.com",
    specialty: "Network Performance Optimization, Traffic Analysis, and Security Protocol Implementation"
},
];



const AdvancedTeamCarousel = () => {
  const navigate = useNavigate(); // for redirection
  // Ensure teamMembers is always an array
  const validTeamMembers = Array.isArray(teamMembers) ? teamMembers : [];

  // If no team members, provide a default state
  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = useRef(null);

  // Adjust sliding logic to handle empty or small arrays
  useEffect(() => {
    if (validTeamMembers.length <= 4) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        (prevIndex + 1) % Math.ceil(validTeamMembers.length / 4)
      );
    }, 10000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, [validTeamMembers.length]);

  const handleCardClick = async (member) => {
    try {
      // Convert the email to Base64
      const encodedEmail = btoa(member.email); // Use `btoa` to encode the email to Base64

      const response = await fetch('/api/tm/bio', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ em: encodedEmail }), // Use the encoded email here
      });

      if (response.ok) {
        const data = await response.json();
        const encodedName = encodeURIComponent(member.name);
        //console.log("data", data)

        navigate(`/${encodedName}`, {
          state: {
            profileData: data,
            fallbackImage: member.image, // pass the image here
          }
        });
        
      } else {
        // Silent fail — do nothing
        //console.log(`No profile found for: ${member.name}`);
      }
    } catch (err) {
      //console.error('Server error:', err); // You may log this for dev but don't show to users
    }
  };

  const socialLinks = {
    linkedin: "https://x.com/0X10LayerFF?t=aJ36mJOzE2ymNUd9LBpokg&s=08",
    twitter: "https://x.com/0X10LayerFF?t=aJ36mJOzE2ymNUd9LBpokg&s=08",
    instagram: "https://www.instagram.com/0x10.layerff?igsh=dXU4NmJyMjFtdzI4&utm_source=qr"
  };

  const TeamMemberCard = ({ member }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="relative group"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="bg-gray-900 rounded-2xl overflow-hidden shadow-2xl border border-gray-800 transform transition-all duration-300 hover:scale-105">
          <div className="relative">
            <img 
              src={member.image || '/api/placeholder/400/400'} 
              alt={member.name || 'Team Member'} 
              className="w-full h-64 object-top filter brightness-75 group-hover:brightness-100 transition-all duration-300"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          </div>
          
          <div className="p-6">
            <h3 className="text-xl font-bold text-white flex items-center">
              {member.name || 'Anonymous Expert'}
              <ShieldCheck className="ml-2 text-blue-500 w-5 h-5" />
            </h3>
            <p className="text-gray-400 mb-2">{member.role || 'Cybersecurity Professional'}</p>
            
            <AnimatePresence>
              {isHovered && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  className="absolute inset-x-0 bottom-20 bg-gray-800/90 backdrop-blur-sm p-4 rounded-xl shadow-lg"
                >
                  <p className="text-sm text-gray-300 italic">
                    "{member.specialty || 'Advancing Cybersecurity Frontiers'}"
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
            
            <div className="flex space-x-3 mt-4">
              {Object.entries(socialLinks).map(([platform, baseUrl]) => (
                <a
                  key={platform}
                  href={`${baseUrl}${(member.name || '').toLowerCase().replace(' ', '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  {platform === 'linkedin' && <LinkedinIcon className="w-5 h-5" />}
                  {platform === 'twitter' && <TwitterIcon className="w-5 h-5" />}
                  {platform === 'instagram' && <Instagram className="w-5 h-5" />}
                </a>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  // Render empty state if no team members
  if (validTeamMembers.length === 0) {
    return (
      <section id="team" className="bg-gray-950 py-16 text-center">
        <div className="max-w-7xl mx-auto px-6">
          <UserX className="mx-auto w-24 h-24 text-gray-600 mb-6" />
          <h2 className="text-3xl font-bold text-white mb-4">
            Our Talent Roster is Loading
          </h2>
          <p className="text-gray-400">
            Our team of cybersecurity experts will be introduced soon.
          </p>
        </div>
      </section>
    );
  }

  const renderTeamSlide = (startIndex) => {
    const membersToShow = validTeamMembers.slice(startIndex, startIndex + 4);
    return (
      <motion.div
        key={startIndex}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
        className="grid md:grid-cols-4 gap-8"
      >
        {membersToShow.map((member, idx) => (
          <div key={idx} onClick={() => handleCardClick(member)} className="cursor-pointer">
            <TeamMemberCard member={member} />
          </div>
        ))}
      </motion.div>
    );
  };

  return (
    <section id="team" className="bg-gray-950 py-16 relative overflow-hidden">
      <div className="absolute inset-0 opacity-20">
        <Waves className="w-full h-full text-blue-900" />
      </div>
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4 text-white flex justify-center items-center">
            <ShieldCheck className="mr-4 text-blue-500" />
            Our Cyber Intelligence Experts
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
          Meet our enthusiastic team of Cybersecurity Specialists
          </p>
        </div>

        <AnimatePresence mode="wait">
          {renderTeamSlide(currentIndex * 4)}
        </AnimatePresence>

        {/* Slide Indicators */}
        {validTeamMembers.length > 4 && (
          <div className="flex justify-center mt-8 space-x-3">
            {Array.from({ length: Math.ceil(validTeamMembers.length / 4) }).map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  currentIndex === idx 
                    ? 'bg-blue-500 w-6' 
                    : 'bg-gray-600 hover:bg-gray-500'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default AdvancedTeamCarousel;
